import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play } from "lucide-react";
import { fadeUp, viewport } from "@/components/anim";

export const VideoShowcase = ({ src }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [60, -60]);

  return (
    <section
      ref={ref}
      data-testid="video-showcase-section"
      className="relative py-24 sm:py-32 bg-[#0A0D14] border-y border-[#14f19514] overflow-hidden"
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
        >
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#14F195] flex items-center gap-2">
            <Play className="w-4 h-4" /> / The Tape Doesn't Lie
          </span>
          <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white leading-[1.05]">
            Real charts. <br />
            <span className="text-[#14F195] text-glow-green">Real prints.</span>
          </h2>
          <p className="mt-6 max-w-md text-[#94A3B8] leading-relaxed">
            This is what an insider call looks like from entry to peak — the exact plays members
            get pinged on before the crowd ever sees them. Watch the alpha unfold in real time.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {["Entry called at $7.7K", "Peaked at $2.2M", "289X live"].map((t) => (
              <span
                key={t}
                className="rounded-full border border-[#14f19533] bg-[#14f1950f] px-4 py-2 font-mono text-xs text-[#14F195]"
              >
                {t}
              </span>
            ))}
          </div>
        </motion.div>

        <motion.div style={{ y }} className="relative">
          <div
            className="relative rounded-[2rem] overflow-hidden border border-[#14f19533] glass p-2"
            style={{ boxShadow: "0 50px 130px -50px rgba(20,241,149,0.55)" }}
          >
            <div className="absolute top-4 left-4 z-10 flex items-center gap-2 rounded-full bg-[#05060A]/80 backdrop-blur px-3 py-1.5 border border-[#14f19533]">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF5470] opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FF5470]" />
              </span>
              <span className="font-mono text-[10px] uppercase tracking-wider text-white">Insider Tape</span>
            </div>
            <video
              data-testid="showcase-video"
              src={src}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              className="w-full rounded-[1.5rem] aspect-[9/16] max-h-[620px] object-cover bg-[#05060A]"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};
